import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  AutoAwesome as AutoAwesomeIcon,
  DeleteOutlineOutlined as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  PhotoAlbum as PhotoAlbumIcon,
  SaveOutlined as SaveOutlinedIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../../api/api";

const EMPTY_ALBUM_FORM = {
  AlbumTitle: "",
  AlbumDescription: "",
  EventDate: "",
  IsActive: true,
  CoverImage: null,
};

const EMPTY_UPLOAD_ROW = {
  files: [],
  description: "",
  startDisplayOrder: "",
};

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getErrorMessage(error, fallbackMessage) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 413) {
    return "Upload failed: the selected files are too large for the server limit. Reduce the batch size or compress the images and try again.";
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  const validationErrors = data?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors).find(
      (value) => Array.isArray(value) && value.length > 0,
    );

    if (firstError?.[0]) {
      return firstError[0];
    }
  }

  return error?.message || fallbackMessage;
}

function normalizeBoolean(value, fallback = true) {
  if ([true, 1, "1", "true"].includes(value)) {
    return true;
  }

  if ([false, 0, "0", "false"].includes(value)) {
    return false;
  }

  return fallback;
}

function normalizeAlbum(rawAlbum) {
  return {
    id: rawAlbum?.AlbumId || rawAlbum?.albumId || rawAlbum?.id,
    title: rawAlbum?.AlbumTitle || rawAlbum?.albumTitle || "",
    description: rawAlbum?.AlbumDescription || rawAlbum?.albumDescription || "",
    eventDate: (rawAlbum?.EventDate || rawAlbum?.eventDate || "").slice(0, 10),
    coverImage: rawAlbum?.CoverImage || rawAlbum?.coverImage || "",
    isActive: normalizeBoolean(rawAlbum?.IsActive ?? rawAlbum?.isActive, true),
    imagesCount: Number(rawAlbum?.ImagesCount || rawAlbum?.imagesCount || 0),
    raw: rawAlbum,
  };
}

function normalizeImage(rawImage) {
  return {
    id:
      rawImage?.GalleryImageId || rawImage?.galleryImageId || rawImage?.ImageId,
    title: rawImage?.ImageTitle || rawImage?.imageTitle || "",
    description: rawImage?.ImageDescription || rawImage?.imageDescription || "",
    imageUrl: rawImage?.ImageUrl || rawImage?.imageUrl || "",
    displayOrder: rawImage?.DisplayOrder ?? rawImage?.displayOrder ?? 0,
    isActive: normalizeBoolean(rawImage?.IsActive ?? rawImage?.isActive, true),
    raw: rawImage,
  };
}

function buildAlbumFormData(form) {
  const formData = new FormData();
  formData.append("AlbumTitle", form.AlbumTitle);
  formData.append("AlbumDescription", form.AlbumDescription);
  formData.append("EventDate", form.EventDate || "");
  formData.append("IsActive", form.IsActive ? "1" : "0");

  if (form.CoverImage) {
    formData.append("CoverImage", form.CoverImage);
  }

  return formData;
}

function buildImagesFormData(rows) {
  const formData = new FormData();

  rows.forEach((row) => {
    row.files.forEach((file, index) => {
      formData.append("Images[]", file);
      formData.append("ImageTitles[]", "");
      formData.append("ImageDescriptions[]", row.description || "");
      formData.append(
        "DisplayOrders[]",
        row.startDisplayOrder === ""
          ? String(index)
          : String(Number(row.startDisplayOrder) + index),
      );
    });
  });

  return formData;
}

function AlbumSummaryCard({ label, value, tone = "default" }) {
  const gradients = {
    default: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    warm: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    success: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: gradients[tone] || gradients.default,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} color="#0f172a">
        {value}
      </Typography>
    </Paper>
  );
}

function AddGalleryImages() {
  const currentUser = useSelector((state) => state.user.user) || {};
  const token =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";

  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState("");
  const [selectedAlbumImages, setSelectedAlbumImages] = useState([]);
  const [albumForm, setAlbumForm] = useState(EMPTY_ALBUM_FORM);
  const [uploadRows, setUploadRows] = useState([{ ...EMPTY_UPLOAD_ROW }]);
  const [loading, setLoading] = useState(true);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [submittingAlbum, setSubmittingAlbum] = useState(false);
  const [submittingImages, setSubmittingImages] = useState(false);
  const [pendingAlbumDeleteId, setPendingAlbumDeleteId] = useState("");
  const [pendingImageDeleteId, setPendingImageDeleteId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedCoverName, setSelectedCoverName] = useState("");

  const selectedAlbum = useMemo(
    () =>
      albums.find((album) => String(album.id) === String(selectedAlbumId)) ||
      null,
    [albums, selectedAlbumId],
  );

  const summary = useMemo(
    () => ({
      totalAlbums: albums.length,
      activeAlbums: albums.filter((album) => album.isActive).length,
      totalImages: albums.reduce(
        (total, album) => total + Number(album.imagesCount || 0),
        0,
      ),
    }),
    [albums],
  );

  const selectedUploadFiles = useMemo(
    () => uploadRows.flatMap((row) => row.files),
    [uploadRows],
  );

  const totalUploadSize = useMemo(
    () =>
      selectedUploadFiles.reduce(
        (total, file) => total + Number(file?.size || 0),
        0,
      ),
    [selectedUploadFiles],
  );

  useEffect(() => {
    let active = true;

    async function loadAlbums() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getGalleryAlbums({ IsActive: 1 }, token);
        const responseData = response?.data || response || {};
        const albumList = Array.isArray(responseData?.albums)
          ? responseData.albums
          : Array.isArray(responseData)
            ? responseData
            : [];

        if (active) {
          const normalizedAlbums = albumList.map(normalizeAlbum);
          setAlbums(normalizedAlbums);
          if (normalizedAlbums[0]?.id) {
            setSelectedAlbumId(String(normalizedAlbums[0].id));
          }
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load gallery albums."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbums();

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;

    async function loadAlbumDetails() {
      if (!selectedAlbumId) {
        setSelectedAlbumImages([]);
        return;
      }

      try {
        setAlbumLoading(true);
        const response = await api.getGalleryAlbumById(
          selectedAlbumId,
          { IsActive: 1 },
          token,
        );
        const responseData = response?.data || response || {};
        const imageList = Array.isArray(responseData?.images)
          ? responseData.images
          : [];

        if (active) {
          setSelectedAlbumImages(imageList.map(normalizeImage));
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load album images."));
        }
      } finally {
        if (active) {
          setAlbumLoading(false);
        }
      }
    }

    loadAlbumDetails();

    return () => {
      active = false;
    };
  }, [selectedAlbumId, token]);

  const handleAlbumFormChange = (field, value) => {
    setAlbumForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUploadRowChange = (index, field, value) => {
    setUploadRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const refreshAlbums = async (nextAlbumId) => {
    const response = await api.getGalleryAlbums({ IsActive: 1 }, token);
    const responseData = response?.data || response || {};
    const albumList = Array.isArray(responseData?.albums)
      ? responseData.albums
      : Array.isArray(responseData)
        ? responseData
        : [];
    const normalizedAlbums = albumList.map(normalizeAlbum);

    setAlbums(normalizedAlbums);

    if (nextAlbumId) {
      setSelectedAlbumId(String(nextAlbumId));
      return;
    }

    if (
      selectedAlbumId &&
      normalizedAlbums.some((album) => String(album.id) === String(selectedAlbumId))
    ) {
      return;
    }

    setSelectedAlbumId(normalizedAlbums[0]?.id ? String(normalizedAlbums[0].id) : "");
  };

  const handleCreateAlbum = async () => {
    if (!albumForm.AlbumTitle.trim()) {
      setError("Album title is required.");
      return;
    }

    try {
      setSubmittingAlbum(true);
      setError("");
      setMessage("");
      const response = await api.createGalleryAlbum(
        buildAlbumFormData(albumForm),
        token,
      );
      const responseData = response?.data || response || {};
      const createdAlbum = normalizeAlbum(responseData?.album || {});
      await refreshAlbums(createdAlbum.id);
      setAlbumForm(EMPTY_ALBUM_FORM);
      setSelectedCoverName("");
      setMessage(responseData?.message || "Album created successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to create album."));
    } finally {
      setSubmittingAlbum(false);
    }
  };

  const handleUpdateAlbum = async () => {
    if (!selectedAlbum?.id) {
      setError("Select an album to update.");
      return;
    }

    try {
      setSubmittingAlbum(true);
      setError("");
      setMessage("");
      const response = await api.updateGalleryAlbum(
        selectedAlbum.id,
        buildAlbumFormData({
          AlbumTitle: selectedAlbum.title,
          AlbumDescription: selectedAlbum.description,
          EventDate: selectedAlbum.eventDate,
          IsActive: selectedAlbum.isActive,
          CoverImage: null,
        }),
        token,
      );
      const responseData = response?.data || response || {};
      await refreshAlbums(selectedAlbum.id);
      setMessage(responseData?.message || "Album updated successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to update album."));
    } finally {
      setSubmittingAlbum(false);
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    try {
      setPendingAlbumDeleteId(String(albumId));
      setError("");
      setMessage("");
      await api.deleteGalleryAlbum(albumId, token);
      await refreshAlbums();
      setSelectedAlbumImages([]);
      setMessage("Album deleted successfully.");
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete album."));
    } finally {
      setPendingAlbumDeleteId("");
    }
  };

  const handleUploadImages = async () => {
    if (!selectedAlbum?.id) {
      setError("Create or select an album before uploading images.");
      return;
    }

    const validRows = uploadRows.filter((row) => row.files.length > 0);

    if (validRows.length === 0) {
      setError("Choose one or more image files.");
      return;
    }

    try {
      setSubmittingImages(true);
      setError("");
      setMessage("");
      const response = await api.addGalleryAlbumImages(
        selectedAlbum.id,
        buildImagesFormData(validRows),
        token,
      );
      const responseData = response?.data || response || {};
      await refreshAlbums(selectedAlbum.id);
      const albumResponse = await api.getGalleryAlbumById(
        selectedAlbum.id,
        { IsActive: 1 },
        token,
      );
      const albumData = albumResponse?.data || albumResponse || {};
      setSelectedAlbumImages(
        Array.isArray(albumData?.images)
          ? albumData.images.map(normalizeImage)
          : [],
      );
      setUploadRows([{ ...EMPTY_UPLOAD_ROW }]);
      setMessage(responseData?.message || "Images uploaded successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to upload images."));
    } finally {
      setSubmittingImages(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setPendingImageDeleteId(String(imageId));
      setError("");
      setMessage("");
      await api.deleteGalleryImage(imageId, token);
      await refreshAlbums(selectedAlbum?.id);
      setSelectedAlbumImages((prev) =>
        prev.filter((image) => String(image.id) !== String(imageId)),
      );
      setMessage("Image deleted successfully.");
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete image."));
    } finally {
      setPendingImageDeleteId("");
    }
  };

  const handleSelectedAlbumFieldChange = (field, value) => {
    setAlbums((prev) =>
      prev.map((album) =>
        String(album.id) === String(selectedAlbumId)
          ? {
              ...album,
              [field]: value,
            }
          : album,
      ),
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        background:
          "radial-gradient(circle at top left, rgba(59, 130, 246, 0.10), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 5,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.96) 100%)",
              color: "white",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Stack spacing={2}>
              <Chip
                icon={<AutoAwesomeIcon />}
                label="Admin gallery workspace"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                  "& .MuiChip-icon": { color: "#93c5fd" },
                }}
              />
              <Box>
                <Typography variant="h4" fontWeight={800}>
                  Create albums and publish event photos
                </Typography>
                <Typography sx={{ mt: 1.25, color: "rgba(255,255,255,0.76)" }}>
                  Build the album, set the cover, then add batches of images
                  with titles and display order from one page.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Albums"
                value={summary.totalAlbums}
                tone="default"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Active albums"
                value={summary.activeAlbums}
                tone="success"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Published images"
                value={summary.totalImages}
                tone="warm"
              />
            </Grid>
          </Grid>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {message ? <Alert severity="success">{message}</Alert> : null}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Create New Album
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                      Use this form for the main album details and optional cover
                      image.
                    </Typography>
                  </Box>

                  <TextField
                    label="Album title"
                    value={albumForm.AlbumTitle}
                    onChange={(event) =>
                      handleAlbumFormChange("AlbumTitle", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label="Album description"
                    value={albumForm.AlbumDescription}
                    onChange={(event) =>
                      handleAlbumFormChange(
                        "AlbumDescription",
                        event.target.value,
                      )
                    }
                    fullWidth
                    multiline
                    minRows={4}
                  />
                  <TextField
                    label="Event date"
                    type="date"
                    value={albumForm.EventDate}
                    onChange={(event) =>
                      handleAlbumFormChange("EventDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography fontWeight={700}>Active album</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inactive albums stay hidden from active gallery views.
                      </Typography>
                    </Box>
                    <Switch
                      checked={albumForm.IsActive}
                      onChange={(event) =>
                        handleAlbumFormChange("IsActive", event.target.checked)
                      }
                    />
                  </Stack>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoAlbumIcon />}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {selectedCoverName ? "Change cover image" : "Upload cover image"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        handleAlbumFormChange("CoverImage", file);
                        setSelectedCoverName(file?.name || "");
                      }}
                    />
                  </Button>
                  {selectedCoverName ? (
                    <Typography variant="body2" color="text.secondary">
                      Selected cover: {selectedCoverName}
                    </Typography>
                  ) : null}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleCreateAlbum}
                    disabled={submittingAlbum}
                    startIcon={
                      submittingAlbum ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveOutlinedIcon />
                      )
                    }
                    sx={{
                      borderRadius: 999,
                      py: 1.4,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {submittingAlbum ? "Creating album..." : "Create Album"}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, lg: 7 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Manage Existing Albums
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                        Select an album, adjust metadata, then add more photos
                        to it.
                      </Typography>
                    </Box>
                    {loading ? <CircularProgress size={24} /> : null}
                  </Stack>

                  <TextField
                    select
                    label="Select album"
                    value={selectedAlbumId}
                    onChange={(event) => setSelectedAlbumId(event.target.value)}
                    fullWidth
                    disabled={loading || albums.length === 0}
                  >
                    {albums.map((album) => (
                      <MenuItem key={album.id} value={String(album.id)}>
                        {album.title} ({album.imagesCount} images)
                      </MenuItem>
                    ))}
                  </TextField>

                  {selectedAlbum ? (
                    <>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Album title"
                            value={selectedAlbum.title}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "title",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Event date"
                            type="date"
                            value={selectedAlbum.eventDate}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "eventDate",
                                event.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="Album description"
                            value={selectedAlbum.description}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "description",
                                event.target.value,
                              )
                            }
                            fullWidth
                            multiline
                            minRows={3}
                          />
                        </Grid>
                      </Grid>

                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Switch
                            checked={selectedAlbum.isActive}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "isActive",
                                event.target.checked,
                              )
                            }
                          />
                          <Typography fontWeight={700}>Album is active</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1.25}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteAlbum(selectedAlbum.id)}
                            disabled={
                              pendingAlbumDeleteId === String(selectedAlbum.id)
                            }
                            startIcon={<DeleteOutlineIcon />}
                          >
                            {pendingAlbumDeleteId === String(selectedAlbum.id)
                              ? "Deleting..."
                              : "Delete album"}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleUpdateAlbum}
                            disabled={submittingAlbum}
                            startIcon={<EditOutlinedIcon />}
                          >
                            Save album changes
                          </Button>
                        </Stack>
                      </Stack>

                      <Divider />

                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h6" fontWeight={800}>
                            Add Images to Selected Album
                          </Typography>
                        </Stack>

                        {selectedUploadFiles.length > 0 ? (
                          <Alert severity="info" sx={{ borderRadius: 3 }}>
                            {selectedUploadFiles.length} file
                            {selectedUploadFiles.length > 1 ? "s" : ""} selected
                            with total size {formatBytes(totalUploadSize)}. If
                            the server returns `413`, split the upload into
                            smaller batches.
                          </Alert>
                        ) : null}

                        {uploadRows.map((row, index) => (
                          <Card
                            key={`${index}-${row.files[0]?.name || "empty"}`}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <CardContent>
                              <Stack spacing={2}>
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  spacing={2}
                                  alignItems={{ xs: "stretch", md: "center" }}
                                >
                                  <Button
                                    component="label"
                                    variant="outlined"
                                    sx={{ alignSelf: { xs: "stretch", md: "flex-start" } }}
                                  >
                                    {row.files.length > 0
                                      ? "Change selected images"
                                      : "Choose bulk images"}
                                    <input
                                      hidden
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "files",
                                          Array.from(event.target.files || []),
                                        )
                                      }
                                    />
                                  </Button>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ flex: 1 }}
                                  >
                                    {row.files.length > 0
                                      ? `${row.files.length} image${
                                          row.files.length > 1 ? "s" : ""
                                        } selected`
                                      : "No images selected yet."}
                                  </Typography>
                                </Stack>

                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                      label="Starting display order"
                                      type="number"
                                      value={row.startDisplayOrder}
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "startDisplayOrder",
                                          event.target.value,
                                        )
                                      }
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 8 }}>
                                    <TextField
                                      select
                                      label="Status"
                                      value="active"
                                      fullWidth
                                      disabled
                                    >
                                      <MenuItem value="active">Active</MenuItem>
                                    </TextField>
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      label="Common description (optional)"
                                      value={row.description}
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "description",
                                          event.target.value,
                                        )
                                      }
                                      fullWidth
                                      multiline
                                      minRows={2}
                                    />
                                  </Grid>
                                </Grid>

                                {row.files.length > 0 ? (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      bgcolor: "white",
                                      border: "1px solid rgba(15, 23, 42, 0.08)",
                                      maxHeight: 180,
                                      overflow: "auto",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      sx={{ mb: 1 }}
                                    >
                                      Selected files
                                    </Typography>
                                    <Stack spacing={0.75}>
                                      {row.files.map((file) => (
                                        <Typography
                                          key={file.name}
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {file.name}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  </Box>
                                ) : null}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}

                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleUploadImages}
                          disabled={submittingImages}
                          startIcon={
                            submittingImages ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AddPhotoAlternateIcon />
                            )
                          }
                          sx={{
                            borderRadius: 999,
                            py: 1.35,
                            textTransform: "none",
                            fontWeight: 700,
                          }}
                        >
                          {submittingImages ? "Uploading images..." : "Upload Images"}
                        </Button>
                      </Stack>

                      <Divider />

                      <Stack spacing={1.5}>
                        <Typography variant="h6" fontWeight={800}>
                          Existing Images
                        </Typography>
                        {albumLoading ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography color="text.secondary">
                              Loading album images...
                            </Typography>
                          </Stack>
                        ) : selectedAlbumImages.length === 0 ? (
                          <Typography color="text.secondary">
                            No images added to this album yet.
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {selectedAlbumImages.map((image) => (
                              <Grid key={image.id} size={{ xs: 12, md: 6 }}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    height: "100%",
                                    borderRadius: 3,
                                    border: "1px solid rgba(15, 23, 42, 0.08)",
                                  }}
                                >
                                  {image.imageUrl ? (
                                    <Box
                                      component="img"
                                      src={image.imageUrl}
                                      alt={image.title || "Gallery image"}
                                      sx={{
                                        width: "100%",
                                        height: 220,
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : null}
                                  <CardContent>
                                    <Stack spacing={1.25}>
                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        spacing={1}
                                      >
                                        <Box>
                                          <Typography fontWeight={800}>
                                            {image.title || "Untitled image"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Order: {image.displayOrder}
                                          </Typography>
                                        </Box>
                                        <Chip
                                          size="small"
                                          color={image.isActive ? "success" : "default"}
                                          label={image.isActive ? "Active" : "Inactive"}
                                        />
                                      </Stack>
                                      {image.description ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {image.description}
                                        </Typography>
                                      ) : null}
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleDeleteImage(image.id)}
                                        disabled={
                                          pendingImageDeleteId === String(image.id)
                                        }
                                        startIcon={<DeleteOutlineIcon />}
                                        sx={{ alignSelf: "flex-start" }}
                                      >
                                        {pendingImageDeleteId === String(image.id)
                                          ? "Deleting..."
                                          : "Delete image"}
                                      </Button>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Typography color="text.secondary">
                      {loading
                        ? "Loading albums..."
                        : "Create your first album to begin adding images."}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

export default AddGalleryImages;
